/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { useCurrentUser } from "actions/CurrentUserActions";
import { Analytics, AnalyticsEvent } from "constants/Analytics";
import AppLoading from "components/AppLoading";
import { AppPath } from "components/AppPath";
import {
  AwaitAuth,
  EnforceBrowser,
  EnforceDesktop,
  RedirectIfNotAuthed,
  RequireAuth,
} from "components/shared/RouteHelpers";
import { prerenderedLoadable } from "components/PrerenderedLoadable";
import { Helmet } from "react-helmet";
import {
  useGeneratePageUrl,
  usePageTracker,
} from "utilities/NavigationUtilities";
import { LocaleMatcher } from "./LocaleMatcher";
import { alternateLocalePage } from "utilities/UserSettingsUtilities";
import { useCanonicalize } from "components/AppRouter/useCanonicalize";
import { isRemoveMusicUsecasePageEnabled } from "PhonosConstants";
import { localeShorthands } from "constants/Locales";
import CaptionsPage from "components/UseCases/CaptionsPage";
import BlockedRegionPage from "components/BlockedRegionPage";

const Plans = prerenderedLoadable(() =>
  import(/* webpackChunkName: "PlansPage" */ "components/Plans").then(
    (module) => ({ default: module.Plans })
  )
);

const LandingPage = prerenderedLoadable(
  () => import(/*webpackChunkName: "LandingPage" */ "components/LandingPage")
);

const ProjectShow = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ProjectShow" */ "components/Studio/ProjectShow"
    )
);

const ProjectIndex = React.lazy(
  () =>
    import(/* webpackChunkName: "ProjectsIndex" */ "components/ProjectIndex")
);

const MicrophoneCheck = React.lazy(
  () => import(/* webpackChunkName: "MicCheck" */ "components/MicrophoneCheck")
);

const EnhancedSpeech = React.lazy(
  () =>
    import(/* webpackChunkName: "EnhanceSpeech" */ "components/EnhancedSpeech")
);

const EnhancedSpeechV2EarlyAccessPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "ESV2_EA" */ "components/UseCases/EnhancedSpeechV2EarlyAccessPage"
    )
);

const EnhancedSpeechInfluencersPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "ESV2_IP" */ "components/UseCases/EnhancedSpeechInfluencersPage"
    )
);

const RecordingPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "RecordingPage" */ "components/UseCases/RecordingPage"
    )
);
const TranscriptionPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "TranscriptionPage" */ "components/UseCases/TranscriptionPage"
    )
);
const AudiogramPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "AudiogramPage" */ "components/UseCases/AudiogramPage"
    )
);
const EditAudioPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "EditAudioPage" */ "components/UseCases/EditAudioPage"
    )
);
const RemoveMusicPage = prerenderedLoadable(
  () =>
    import(
      /* webpackChunkName: "RemoveMusicPage" */ "components/UseCases/RemoveMusicPage"
    )
);

const GuidePages = prerenderedLoadable(() =>
  import(/* webpackChunkName: "GuidePages" */ "components/GuidePages").then(
    (module) => {
      return { default: module.GuidePages };
    }
  )
);

const CreatorPages = prerenderedLoadable(() =>
  import(/* webpackChunkName: "CreatorPages" */ "components/CreatorPages").then(
    (module) => {
      return { default: module.CreatorPages };
    }
  )
);

const allAppPaths = Object.keys(AppPath).map(
  (key) => AppPath[key as keyof typeof AppPath]
);

const AppRouterPaths = (): React.ReactElement => {
  const { fetchCurrentUser } = useCurrentUser();
  const match = useRouteMatch();
  usePageTracker();

  useEffect(() => {
    fetchCurrentUser().then((current) => {
      if (current) {
        Analytics.instance.logEvent(AnalyticsEvent.SESSION_LOAD); // TODO: what do we do for unauthenticated users
      }
    });
  }, [fetchCurrentUser]);

  const routes = allAppPaths.map((path: AppPath) => {
    const matchAndPath = `${match.path}${path}`;
    switch (path) {
      case AppPath.ROOT:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <LandingPage />
          </Route>
        );
      case AppPath.STUDIO:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <RedirectIfNotAuthed>
              <EnforceBrowser>
                <ProjectIndex />
              </EnforceBrowser>
            </RedirectIfNotAuthed>
          </Route>
        );
      case AppPath.PROJECT_RECORD_V2:
      case AppPath.PROJECT_SHOW:
        return (
          <Route path={matchAndPath} exact={false} key={path}>
            <RequireAuth>
              <EnforceBrowser>
                <ProjectShow anonymousAccess={false} />
              </EnforceBrowser>
            </RequireAuth>
          </Route>
        );
      case AppPath.TEMPLATE_CREATE:
        return (
          <Route path={matchAndPath} exact={false} key={path}>
            <RequireAuth>
              <EnforceBrowser>
                <ProjectIndex loadTemplate={true} />;
              </EnforceBrowser>
            </RequireAuth>
          </Route>
        );
      case AppPath.ENHANCE_SPEECH:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <AwaitAuth>
              <EnhancedSpeech />
            </AwaitAuth>
          </Route>
        );
      case AppPath.ENHANCE_SPEECH_V2_EARLY_ACCESS_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <EnhancedSpeechV2EarlyAccessPage />
          </Route>
        );
      case AppPath.ENHANCE_SPEECH_INFLUENCERS_PAGE:
      case AppPath.ENHANCE_SPEECH_INFLUENCERS_META_PAGE:
      case AppPath.ENHANCE_SPEECH_INFLUENCERS_GOOGLE_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <EnhancedSpeechInfluencersPage />
          </Route>
        );
      case AppPath.MICROPHONE_CHECK:
        return (
          <Route path={matchAndPath} exact={false} key={path}>
            <AwaitAuth>
              <EnforceDesktop>
                <MicrophoneCheck />
              </EnforceDesktop>
            </AwaitAuth>
          </Route>
        );
      case AppPath.FEATURES:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <Redirect to={AppPath.PLANS} />
          </Route>
        );
      case AppPath.PLANS:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <Plans />
          </Route>
        );
      case AppPath.RECORD_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <RecordingPage />
          </Route>
        );
      case AppPath.TRANSCRIPTION_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <TranscriptionPage />
          </Route>
        );
      case AppPath.CONVERT_AUDIO_TO_VIDEO_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <AudiogramPage />
          </Route>
        );
      case AppPath.ADD_CAPTIONS_TO_VIDEO_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <CaptionsPage />
          </Route>
        );
      case AppPath.REMOVE_MUSIC_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            {isRemoveMusicUsecasePageEnabled ? (
              <RemoveMusicPage />
            ) : (
              <Redirect to={match.url} />
            )}
          </Route>
        );
      case AppPath.EDIT_AUDIO_PAGE:
      case AppPath.EDIT_WITH_PODCAST_PAGE:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <EditAudioPage />
          </Route>
        );
      case AppPath.ANONYMOUS_RECORDING:
        return (
          <Route path={matchAndPath} exact={false} key={path}>
            <AwaitAuth>
              <EnforceBrowser>
                <ProjectShow anonymousAccess={true} />
              </EnforceBrowser>
            </AwaitAuth>
          </Route>
        );
      case AppPath.GUIDES:
        return (
          <Route path={matchAndPath} exact={false} key={path}>
            <GuidePages />
          </Route>
        );
      case AppPath.CREATORS:
        return (
          <Route path={matchAndPath} exact={false} key={path}>
            <CreatorPages />
          </Route>
        );
      case AppPath.BLOCKED_REGION:
        return (
          <Route path={matchAndPath} exact={true} key={path}>
            <BlockedRegionPage />
          </Route>
        );
      default:
        // This will not compile if cases are not exhaustive
        const _exhaustiveCheck: never = path;
        throw new Error(`Unhandled AppPath case: ${path}`);
    }
  });

  const generatePageUrl = useGeneratePageUrl();

  const rootPath = generatePageUrl(AppPath.ROOT);

  // Default route
  routes.push(
    <Route key="default">
      <Redirect to={rootPath} />
    </Route>
  );

  const location = useLocation();
  const canonical = generatePageUrl(location.pathname, {
    absolute: true,
    locale: "none",
  });

  useCanonicalize();

  return (
    <Suspense fallback={<AppLoading />}>
      <Helmet>
        <html lang={I18n.locale} />
        <title>{I18n.t("meta.title")}</title>
        <meta name="description" content={I18n.t("meta.description")} />
        <meta property="og:title" content={I18n.t("meta.title")} />
        <meta name="title" content={I18n.t("meta.title")} />
        <link rel="canonical" href={canonical} />
        {[...new Set(Object.values(localeShorthands))].map((shortHand) => {
          const href = generatePageUrl(
            alternateLocalePage(location.pathname, shortHand),
            {
              absolute: true,
              locale: "none",
            }
          );
          return (
            <link
              rel="alternate"
              href={href}
              hrefLang={shortHand}
              key={shortHand}
            />
          );
        })}
      </Helmet>
      <Switch>{routes}</Switch>
    </Suspense>
  );
};

export const AppRouter: React.FC = () => {
  const basename = process.env.REACT_APP_BASE_PATH || "/";

  return (
    <Router basename={basename}>
      <LocaleMatcher>
        <AppRouterPaths />
      </LocaleMatcher>
    </Router>
  );
};
